import { FC, useEffect, useState } from 'react';

import {
  deleteBillingCard,
  getBillingCard,
  setBillingCard,
} from '@shared/api/giftOrder/giftOrder.controller';
import { GiftOrderBillingCard } from '@shared/api/giftOrder/giftOrder.interface';

interface BillingCardSectionProps {
  orderId: string;
}

const maskCardNumber = (num: string): string => {
  const digits = num.replace(/\s/g, '');
  if (digits.length <= 8) return num;
  return `${digits.slice(0, 4)} **** **** ${digits.slice(-4)}`;
};

/**
 * 추가교환 충당용 빌링카드 등록/조회/삭제 (Admin 전용).
 * 상위에서 isAdmin() 게이팅 후 렌더한다.
 */
export const BillingCardSection: FC<BillingCardSectionProps> = ({ orderId }) => {
  const [card, setCard] = useState<GiftOrderBillingCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [cardCompany, setCardCompany] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = () => {
    setLoading(true);
    getBillingCard(orderId)
      .then(setCard)
      .catch((err) => {
        console.error('빌링카드 조회 실패:', err);
        setCard(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let alive = true;
    getBillingCard(orderId)
      .then((res) => {
        if (alive) setCard(res);
      })
      .catch((err) => {
        console.error('빌링카드 조회 실패:', err);
        if (alive) setCard(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderId]);

  const startEdit = () => {
    setCardCompany(card?.cardCompany ?? '');
    setCardNumber(card?.cardNumber ?? '');
    setExpiry(card?.expiry ?? '');
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!cardCompany.trim() || !cardNumber.trim() || !expiry.trim()) {
      setError('카드사·카드번호·유효기간을 모두 입력해주세요.');
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(expiry.trim())) {
      setError('유효기간은 MM/YY 형식으로 입력해주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setBillingCard(orderId, {
        cardCompany: cardCompany.trim(),
        cardNumber: cardNumber.replace(/\s/g, ''),
        expiry: expiry.trim(),
      });
      setEditing(false);
      refetch();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '카드 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('등록된 빌링카드를 삭제하시겠습니까?')) return;
    setSubmitting(true);
    try {
      await deleteBillingCard(orderId);
      setCard(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : '카드 삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shrink-0 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-sky-800">충당용 빌링카드</span>
          <span className="text-xs text-sky-500">추가교환 추가결제용 (선택)</span>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            {card ? (
              <>
                <button
                  onClick={startEdit}
                  disabled={submitting}
                  className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-bold text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                >
                  수정
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  삭제
                </button>
              </>
            ) : (
              !loading && (
                <button
                  onClick={startEdit}
                  className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-700"
                >
                  카드 등록
                </button>
              )
            )}
          </div>
        )}
      </div>

      {!editing && (
        <div className="mt-2 border-t border-sky-200 pt-2 text-xs">
          {loading ? (
            <span className="text-sky-400">조회 중…</span>
          ) : card ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sky-700">
              <span className="font-medium">{card.cardCompany}</span>
              <span className="font-mono">{maskCardNumber(card.cardNumber)}</span>
              <span>유효 {card.expiry}</span>
              {card.registeredBy?.name && (
                <span className="text-sky-400">등록: {card.registeredBy.name}</span>
              )}
            </div>
          ) : (
            <span className="text-sky-400">등록된 카드 없음 (재고 충당 시 생략 가능)</span>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-2 space-y-2 border-t border-sky-200 pt-2">
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={cardCompany}
              onChange={(e) => setCardCompany(e.target.value)}
              placeholder="카드사 (예: 신한)"
              className="w-28 rounded-lg border border-sky-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="카드번호"
              inputMode="numeric"
              className="flex-1 rounded-lg border border-sky-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
            <input
              type="text"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              placeholder="MM/YY"
              className="w-20 rounded-lg border border-sky-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
          </div>
          {error && <p className="text-xs font-medium text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              className="rounded-lg border border-sky-300 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              className="rounded-lg bg-sky-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {submitting ? '저장중…' : '저장'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
